/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AddIcon from '@mui/icons-material/Add';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useParams } from 'react-router-dom';
import HelpEdit from './HelpEdit';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import { fetchAllArticles, IArticleData } from '../../../../../../redux/slices/article/getAllArticleSlice';
import { getUserDetails } from '../../../../../../utils/orgName';
import RenderHTML from '../../../../../../components/RenderHTML';

export type Article = IArticleData;

const ContentSettings = () => {
  const params = useParams();
  const grpId = params.id || '';
  const orgId = params.orgId || '';
  const dispatch = useAppDispatch();
  const articleState = useAppSelector((state) => state.allArticle);
  const articles = articleState.data?.ArticleList || [];

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [action, setAction] = useState<'Add' | 'Edit'>('Add');
  const [selectedArticleData, setSelectedArticleData] = useState<IArticleData | undefined>(undefined);

  useEffect(() => {
    const { location } = getUserDetails();
    dispatch(fetchAllArticles({ orgId, groupId: grpId, helpSettings: true, useCache: false, role: 'mentee' }));
    if (!orgId && !grpId && location) {
      dispatch(fetchAllArticles({ orgId: '', groupId: '', helpSettings: true, useCache: false }));
    }
  }, [dispatch, grpId, orgId]);

  useEffect(() => {
    if (articles.length && !selectedId) {
      setSelectedId(articles[0].articleId);
      setSelectedArticleData(articles[0]);
    }
  }, [articles, selectedId]);

  const selectedArticle = useMemo(() => articles.find((a) => a.articleId === selectedId), [articles, selectedId]);

  const openAddModal = () => {
    setAction('Add');
    setSelectedArticleData(undefined);
    setOpenForm(true);
  };

  const renderHeaderTabs = () => {
    const renderTab = (label: string, selected?: boolean, disabled?: boolean) => (
      <Box
        key={label}
        sx={{
          px: 2.3,
          py: 1.1,
          background: selected ? '#444444' : disabled ? '#151515' : '#2A2A2A',
          color: selected ? '#FDFDFD' : disabled ? '#868686' : '#B6B6B6',
          fontSize: 12,
          fontWeight: selected ? 700 : 400,
          opacity: disabled ? 0.5 : 1
        }}
      >
        {label}
      </Box>
    );

    return (
      <Box sx={{ ml: 'auto', display: 'inline-flex', borderRadius: 1, overflow: 'hidden' }}>
        {renderTab('Program Design', false, true)}
        {renderTab('Configuration', false, false)}
        {renderTab('Resources', true, false)}
      </Box>
    );
  };

  const EmptyState = () => (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ width: 400, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography sx={{ color: '#B6B6B6', fontSize: 16, fontWeight: 600 }}>
          Your Resource Library is empty.
        </Typography>
        <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>Upload resources to continue.</Typography>
      </Box>
      <Button
        onClick={openAddModal}
        sx={{
          px: 1.8,
          py: 1,
          borderRadius: 1,
          background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
          color: '#FDFDFD',
          textTransform: 'none',
          fontSize: 13,
          fontWeight: 600
        }}
      >
        New Content Drop
      </Button>
    </Box>
  );

  const ArticleList = () => (
    <Box
      sx={{
        width: 320,
        borderRight: '1px solid #2A2A2A',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        minHeight: 0
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 600, flex: 1 }}>Articles</Typography>
        <Button
          onClick={openAddModal}
          sx={{
            minWidth: 0,
            width: 30,
            height: 30,
            borderRadius: '50%',
            background: 'none',
            color: '#FDFDFD',
            p: 0,
            fontSize: 18,
            lineHeight: 1,
            '&:hover': { opacity: 0.9 }
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
        </Button>
        <Box
          sx={{
            width: 30,
            height: 30,
            bgcolor: '#1C1C1C',
            borderRadius: '50%',
            opacity: 0.4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#868686',
            pointerEvents: 'none'
          }}
        >
          <FilterListIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ color: '#B6B6B6', fontSize: 14 }}>All ({articles.length})</Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {articles.map((article) => {
          const isActive = article.articleId === selectedId;
          return (
            <Box
              key={article.articleId}
              onClick={() => {
                setSelectedId(article.articleId);
                setSelectedArticleData(article);
              }}
              sx={{
                px: 3,
                py: 2.5,
                display: 'flex',
                gap: 1.5,
                cursor: 'pointer',
                bgcolor: isActive ? '#151515' : 'transparent',
                borderBottom: '1px solid #2A2A2A'
              }}
            >
              <Box
                sx={{
                  width: 114,
                  height: 68,
                  borderRadius: 1,
                  backgroundImage: `url(${article.coverImageUrl || 'https://placehold.co/114x68'})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 500, lineHeight: 1.3 }}>
                  {article.articleName}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 0.5,
                      bgcolor: '#1C1C1C',
                      border: '1px solid #2A2A2A',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <ThumbUpOffAltIcon sx={{ fontSize: 14, color: '#B6B6B6' }} />
                    <Typography sx={{ color: '#B6B6B6', fontSize: 10, fontWeight: 500 }}>{article.upVotes}</Typography>
                  </Box>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 0.5,
                      bgcolor: '#1C1C1C',
                      border: '1px solid #2A2A2A',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <ThumbDownOffAltIcon sx={{ fontSize: 14, color: '#B6B6B6' }} />
                    <Typography sx={{ color: '#B6B6B6', fontSize: 10, fontWeight: 500 }}>
                      {article.downVotes}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 0.5,
                      bgcolor: '#1C1C1C',
                      border: '1px solid #2A2A2A',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <VisibilityOutlinedIcon sx={{ fontSize: 14, color: '#B6B6B6' }} />
                    <Typography sx={{ color: '#B6B6B6', fontSize: 10, fontWeight: 500 }}>{article.views}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const ArticleDetail = () =>
    selectedArticle ? (
      <Box
        sx={{
          flex: 1,
          px: 3,
          py: 3.5,
          bgcolor: '#151515',
          height: '100%',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[selectedArticle.articleViewableTo, ...(selectedArticle.articleViewableUserType || [])]
                .filter(Boolean)
                .map((tag) => (
                  <Box
                    key={tag}
                    sx={{
                      px: 1,
                      py: 0.6,
                      bgcolor: '#1C1C1C',
                      color: '#FDFDFD',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 500,
                      textTransform: 'uppercase'
                    }}
                  >
                    {tag}
                  </Box>
                ))}
            </Box>

            <Typography sx={{ color: '#FDFDFD', fontSize: 30, fontWeight: 700 }}>
              {selectedArticle.articleName}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, color: '#B6B6B6', fontSize: 12 }}>
              <span>{selectedArticle.createdAt ? new Date(selectedArticle.createdAt * 1000).toDateString() : ''}</span>
              <span>{selectedArticle.readTime}</span>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
              {(selectedArticle.tags || []).map((t) => (
                <Box
                  key={t}
                  sx={{
                    px: 1,
                    py: 0.4,
                    bgcolor: '#2A2A2A',
                    borderRadius: 20,
                    color: '#FDFDFD',
                    fontSize: 12
                  }}
                >
                  {t}
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                width: 300,
                px: 1.2,
                py: 1.1,
                borderRadius: 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.25,
                bgcolor: 'transparent'
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  backgroundImage: 'url(https://placehold.co/30x30)',
                  backgroundSize: 'cover'
                }}
              />
              <Box
                sx={{
                  color: '#FDFDFD',
                  fontSize: 16,
                  fontWeight: 500,
                  lineHeight: '17.6px'
                }}
              >
                {selectedArticle.authorName}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: '#444' }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, color: 'white !important' }}>
            <RenderHTML htmlContent={selectedArticle.articleDescription || ''} />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                sx={{
                  px: 1,
                  py: 0.6,
                  borderRadius: 0.5,
                  bgcolor: '#1C1C1C',
                  border: '1px solid #2A2A2A',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <ThumbUpOffAltIcon sx={{ fontSize: 18, color: '#B6B6B6' }} />
                <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 500 }}>
                  {selectedArticle.upVotes}
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 1,
                  py: 0.6,
                  borderRadius: 0.5,
                  bgcolor: '#1C1C1C',
                  border: '1px solid #2A2A2A',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <ThumbDownOffAltIcon sx={{ fontSize: 18, color: '#B6B6B6' }} />
                <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 500 }}>
                  {selectedArticle.downVotes}
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 1,
                  py: 0.6,
                  borderRadius: 0.5,
                  bgcolor: '#1C1C1C',
                  border: '1px solid #2A2A2A',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <VisibilityOutlinedIcon sx={{ fontSize: 18, color: '#B6B6B6' }} />
                <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 500 }}>
                  {selectedArticle.views}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: '#444' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700 }}>Was this content helpful?</Typography>
            <Box
              sx={{
                px: 1,
                py: 0.6,
                borderRadius: 0.5,
                bgcolor: '#1C1C1C',
                border: '1px solid #2A2A2A',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <ThumbUpOffAltIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />
            </Box>
            <Box
              sx={{
                px: 1,
                py: 0.6,
                borderRadius: 0.5,
                bgcolor: '#1C1C1C',
                border: '1px solid #2A2A2A',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <ThumbDownOffAltIcon sx={{ fontSize: 18, color: '#FDFDFD' }} />
            </Box>
          </Box>
        </Box>
      </Box>
    ) : null;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#1C1C1C',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        {articles.length === 0 ? (
          <EmptyState />
        ) : (
          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', height: '100%', minHeight: 0 }}>
            <ArticleList />
            <ArticleDetail />
          </Box>
        )}
      </Box>

      <HelpEdit
        openForm={openForm}
        handleClickCloseForm={() => setOpenForm(false)}
        setOpenForm={setOpenForm}
        selectedActiveId={selectedArticleData?.articleId || ''}
        singleListData={selectedArticleData || {}}
        action={action}
        setAction={setAction}
      />
    </Box>
  );
};

export default ContentSettings;
